<script setup>
import { ref, nextTick, computed, defineProps, defineEmits, watch, onMounted, onUnmounted } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import 'swiper/css';

import CustomPagination from './../ui/CustomPagination.vue';
import FeedPickerSlideContent from './FeedPickerSlideContent.vue';

const swiperInstance = ref(null);
const activeIndex = ref(0);
const modelData = ref({});
const screenWidth = ref(window.innerWidth);
const visibleSteps = ref(1);
const specialNeedsLoading = ref(false);

const props = defineProps({
  data: { type: Object, default: () => ({}) },
  popupsData: { type: Object, default: () => ({}) },
  loadSpecialNeeds: { type: Function, required: true },
});

const emit = defineEmits(['trigger-search', 'show-popup', 'model-change']);

const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth;
};

onMounted(() => window.addEventListener('resize', updateScreenWidth));
onUnmounted(() => window.removeEventListener('resize', updateScreenWidth));

const isMobile = computed(() => screenWidth.value <= 768);

const hasAnyValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return value !== undefined && value !== null && value !== '';
};

const normalizeToArray = (value) => {
  if (Array.isArray(value)) return value.map(String);
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
};

const normalizeTypesForRequest = (model) => {
  const normalizedTypes = normalizeToArray(model.types);
  const normalizedTextures = normalizeToArray(model.textures);

  const next = {
    ...model,
    types: normalizedTypes.length > 0 ? normalizedTypes : ['dry', 'wet'],
    textures: normalizedTextures,
  };

  if (next.pet_type === 'cat') {
    next.pet_size = 'cat';
  }

  if (next.pet_type === 'dog') {
    delete next.is_sterilized;
    next.textures = [];
  }

  if (
      next.is_sterilized === undefined ||
      next.is_sterilized === null ||
      next.is_sterilized === ''
  ) {
    delete next.is_sterilized;
  } else {
    next.is_sterilized = Number(next.is_sterilized);
  }

  return next;
};

const isAnswered = (val) => {
  if (val === null || val === undefined) return false;
  if (typeof val === 'string') return val.trim().length > 0;
  if (Array.isArray(val)) return val.length > 0;
  return true;
};

const countAnsweredInFlow = (flow) => {
  return flow.reduce((acc, q) => {
    const key = q?.value;
    if (!key) return acc;
    return acc + (isAnswered(modelData.value[key]) ? 1 : 0);
  }, 0);
};

const isCatKitten = computed(() =>
    modelData.value?.pet_type === 'cat' &&
    modelData.value?.pet_age === 'cat_kitten'
);

const isCatYoung = computed(() =>
    modelData.value?.pet_type === 'cat' &&
    modelData.value?.pet_age === 'cat_young'
);

const isCatAdult = computed(() =>
    modelData.value?.pet_type === 'cat' &&
    modelData.value?.pet_age === 'cat_adult'
);

const getSearchModel = () => {
  return normalizeTypesForRequest(modelData.value);
};

const selectedTypes = computed(() => normalizeToArray(modelData.value?.types));

const isNotSterilizedCatYoung = computed(() =>
    isCatYoung.value &&
    Number(modelData.value?.is_sterilized) === 0
);

const hasWetType = computed(() =>
    selectedTypes.value.includes('wet')
);

const shouldShowTextures = computed(() =>
    hasWetType.value &&
    (
        isCatKitten.value ||
        isCatYoung.value
    )
);

const prepareQuestion = (question) => {
  if (question?.value !== 'textures') {
    return question;
  }

  const options = Array.isArray(question.options)
      ? [...question.options]
      : [];

  return {
    ...question,
    options: isNotSterilizedCatYoung.value
        ? options.filter((option) => option?.value !== 'pate')
        : options,
  };
};

const questionsFlow = computed(() => {
  const base = Array.isArray(props.data.type) ? [...props.data.type] : [];
  const petTypeChoice = modelData.value?.pet_type;

  if (petTypeChoice === 'cat' && Array.isArray(props.data.cats)) {
    const catFlow = props.data.cats
        .filter((q) => {
          // Котенок: шаг стерилизации убираем
          if (q?.value === 'is_sterilized' && isCatKitten.value) {
            return false;
          }

          // Кошка 7+: шаг стерилизации убираем
          if (q?.value === 'is_sterilized' && isCatAdult.value) {
            return false;
          }

          // Текстуры показываем только: котенок + влажный корм/кошка 1-7 + не стерилизована + влажный корм
          if (q?.value === 'textures') {
            return shouldShowTextures.value;
          }

          // Потребности скрываем, если вместо них показываем текстуры. Также для кошки 7+ оставляем старую логику: special_needs не показываем
          if (q?.value === 'special_needs') {
            if (shouldShowTextures.value) return false;
            if (isCatAdult.value) return false;

            return true;
          }

          return true;
        })
        .map(prepareQuestion);

    base.push(...catFlow);
  }

  if (petTypeChoice === 'dog' && Array.isArray(props.data.dogs)) {
    const petSize = modelData.value?.pet_size;
    const petAge = modelData.value?.pet_age;

    const hideDogTypes =
        ['dog_large', 'dog_medium'].includes(petSize) ||
        (['dog_small', 'dog_tiny'].includes(petSize) && petAge === 'dog_puppy');

    const hideDogSpecialNeeds =
        (petSize === 'dog_large' && petAge === 'dog_puppy') ||
        (['dog_small', 'dog_tiny'].includes(petSize) && petAge === 'dog_puppy');

    const dogFlow = props.data.dogs.filter((q) => {
      if (q?.value === 'types' && hideDogTypes) return false;
      if (q?.value === 'special_needs' && hideDogSpecialNeeds) return false;
      return true;
    });

    base.push(...dogFlow);
  }

  return base;
});

const mobileQuestions = computed(() => questionsFlow.value.slice(0, visibleSteps.value));

watch(modelData, (val) => emit('model-change', val || {}), { deep: true, immediate: true });

watch(
    () => modelData.value?.pet_type,
    (newPetType, oldPetType) => {
      if (!newPetType || newPetType === oldPetType) return;

      if (newPetType === 'cat') {
        modelData.value.pet_size = undefined;
        modelData.value.pet_age = undefined;
        modelData.value.types = undefined;
        modelData.value.special_needs = [];
        modelData.value.textures = [];
        modelData.value.is_sterilized = undefined;
      }

      if (newPetType === 'dog') {
        modelData.value.pet_size = undefined;
        modelData.value.pet_age = undefined;
        modelData.value.types = undefined;
        modelData.value.special_needs = [];
        modelData.value.textures = [];
        modelData.value.is_sterilized = undefined;
      }
    }
);

watch(
    () => [
      modelData.value?.pet_type,
      modelData.value?.pet_size,
      modelData.value?.pet_age,
      modelData.value?.types,
      modelData.value?.is_sterilized,
    ],
    ([petType, petSize, petAge, types]) => {
      const normalizedTypes = normalizeToArray(types);

      if (
          petType === 'cat' &&
          ['cat_kitten', 'cat_adult'].includes(petAge) &&
          hasAnyValue(modelData.value.is_sterilized)
      ) {
        delete modelData.value.is_sterilized;
      }

      const shouldShowTexturesForCurrentState =
          petType === 'cat' &&
          normalizedTypes.includes('wet') &&
          (
              petAge === 'cat_kitten' ||
              petAge === 'cat_young'
          );

      const shouldHideSpecialNeeds =
          shouldShowTexturesForCurrentState ||
          (petType === 'cat' && petAge === 'cat_adult');

      const shouldHideDogTypes =
          petType === 'dog' &&
          (
              ['dog_large', 'dog_medium'].includes(petSize) ||
              (['dog_small', 'dog_tiny'].includes(petSize) && petAge === 'dog_puppy')
          );

      const shouldHideDogSpecialNeeds =
          petType === 'dog' &&
          (
              (petSize === 'dog_large' && petAge === 'dog_puppy') ||
              (['dog_small', 'dog_tiny'].includes(petSize) && petAge === 'dog_puppy')
          );

      if (!shouldShowTexturesForCurrentState && hasAnyValue(modelData.value.textures)) {
        modelData.value.textures = [];
      }

      if (shouldHideSpecialNeeds && hasAnyValue(modelData.value.special_needs)) {
        modelData.value.special_needs = [];
      }

      if (shouldHideDogTypes && hasAnyValue(modelData.value.types)) {
        modelData.value.types = [];
      }

      if (shouldHideDogSpecialNeeds && hasAnyValue(modelData.value.special_needs)) {
        modelData.value.special_needs = [];
      }
    },
    { immediate: true, deep: true }
);

const flowSignature = computed(() => questionsFlow.value.map((q) => q?.value).join('|'));

watch(
    flowSignature,
    async () => {
      if (isMobile.value) {
        const answered = countAnsweredInFlow(questionsFlow.value);
        visibleSteps.value = Math.min(Math.max(answered + 1, 1), questionsFlow.value.length);
        return;
      }

      await nextTick();

      const maxIndex = Math.max(questionsFlow.value.length - 1, 0);
      const nextIndex = Math.min(activeIndex.value, maxIndex);

      activeIndex.value = nextIndex;
      swiperInstance.value?.slideTo(nextIndex, 0);
    },
    { immediate: true }
);

watch(
    () => modelData.value?.types,
    () => {
      if (!isMobile.value) return;

      const answered = countAnsweredInFlow(questionsFlow.value);
      visibleSteps.value = Math.min(Math.max(answered + 1, 1), questionsFlow.value.length);
    },
    { deep: true }
);

const onSwiper = (swiper) => {
  swiperInstance.value = swiper;
};

const onSlideChange = () => {
  activeIndex.value = swiperInstance.value?.activeIndex || 0;
};

const goToSlide = (index) => {
  swiperInstance.value?.slideTo(index);
};

const canGoBack = computed(() => activeIndex.value > 0);

const getHeaderOffset = () => {
  const header = document.querySelector('.b-header');
  return header ? header.offsetHeight : 142;
};

const scrollToStep = async (index) => {
  await nextTick();

  const el = document.querySelector(`[data-feedpicker-step="${index}"]`);
  if (!el) return;

  const offset = getHeaderOffset();
  const rect = el.getBoundingClientRect();
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

  window.scrollTo({
    top: rect.top + scrollTop - offset,
    behavior: 'smooth',
  });
};

const getCurrentIndexInFlow = (item, fallbackIndex = 0) => {
  const idx = questionsFlow.value.findIndex((q) => q?.value === item?.value);
  return idx >= 0 ? idx : fallbackIndex;
};

const getNextStep = (item, fallbackIndex = 0) => {
  const actualIndex = getCurrentIndexInFlow(item, fallbackIndex);
  return questionsFlow.value[actualIndex + 1] || null;
};

const isActuallyLastStep = (item, fallbackIndex = 0) => {
  const actualIndex = getCurrentIndexInFlow(item, fallbackIndex);
  return actualIndex === questionsFlow.value.length - 1;
};

const getModelForSpecialNeedsRequest = () => {
  return normalizeTypesForRequest(modelData.value);
};

const fetchSpecialNeedsIfNeeded = async (item, currentIndex = 0) => {
  await nextTick();

  const nextItem = getNextStep(item, currentIndex);
  if (nextItem?.value !== 'special_needs') return;

  specialNeedsLoading.value = true;
  try {
    await props.loadSpecialNeeds(getModelForSpecialNeedsRequest());
  } finally {
    specialNeedsLoading.value = false;
  }
};

const isContinueDisabled = (item, fallbackIndex = 0) => {
  const nextItem = getNextStep(item, fallbackIndex);
  return nextItem?.value === 'special_needs' ? specialNeedsLoading.value : false;
};

const proceedDesktop = async (currentIndex, item) => {
  await nextTick();

  const actualIndex = getCurrentIndexInFlow(item, currentIndex);
  const isLastStep = actualIndex === questionsFlow.value.length - 1;

  if (isLastStep) {
    emit('trigger-search', getSearchModel());
    return;
  }

  await fetchSpecialNeedsIfNeeded(item, currentIndex);
  await nextTick();

  const recalculatedIndex = getCurrentIndexInFlow(item, currentIndex);
  const nextIndex = recalculatedIndex + 1;

  if (nextIndex >= questionsFlow.value.length) {
    emit('trigger-search', getSearchModel());
    return;
  }

  swiperInstance.value?.slideTo(nextIndex);
};

const proceedMobile = async (currentIndex, item) => {
  await nextTick();

  const actualIndex = getCurrentIndexInFlow(item, currentIndex);
  const isLastStep = actualIndex === questionsFlow.value.length - 1;

  if (isLastStep) {
    emit('trigger-search', getSearchModel());
    return;
  }

  await fetchSpecialNeedsIfNeeded(item, currentIndex);
  await nextTick();

  const recalculatedIndex = getCurrentIndexInFlow(item, currentIndex);
  const nextIndex = recalculatedIndex + 1;

  if (nextIndex >= questionsFlow.value.length) {
    emit('trigger-search', getSearchModel());
    return;
  }

  visibleSteps.value = Math.min(nextIndex + 1, questionsFlow.value.length);
  await scrollToStep(nextIndex);
};

const nextQuestion = async (_isLast, currentIndex, item) => {
  if (isMobile.value) {
    await proceedMobile(currentIndex, item);
  } else {
    await proceedDesktop(currentIndex, item);
  }
};

const prevQuestion = () => {
  swiperInstance.value?.slidePrev();
};
</script>

<template>
  <div class="feed-picker__container">
    <div class="feed-picker-slider">

      <!-- MOBILE -->
      <div v-if="isMobile" class="feed-picker-slider__stack">
        <div
            v-for="(item, idx) in mobileQuestions"
            :key="`${item.value}-${idx}`"
            class="feed-picker-slider__stack-item"
            :class="{ '_active': idx === visibleSteps - 1 }"
            :data-feedpicker-step="idx"
        >
          <FeedPickerSlideContent
              :item="item"
              :active-index="idx"
              :display-index="idx"
              v-model="modelData[item.value]"
              :popups-data="popupsData"
              :continue-disabled="isContinueDisabled(item, idx)"
              :is-last-step="isActuallyLastStep(item, idx)"
              @continue="(isLast, emittedItem) => nextQuestion(isLast, idx, emittedItem)"
              @next-slide="(isLast, emittedItem) => nextQuestion(isLast, idx, emittedItem)"
              @show-popup="(content) => emit('show-popup', content)"
          />
        </div>
      </div>

      <!-- DESKTOP -->
      <template v-else>
        <Swiper
            :allow-touch-move="false"
            :speed="600"
            :slides-per-view="'auto'"
            direction="horizontal"
            @swiper="onSwiper"
            @slideChange="onSlideChange"
        >
          <SwiperSlide
              v-for="(item, idx) in questionsFlow"
              :key="`${item.value}-${idx}`"
          >
            <FeedPickerSlideContent
                :item="item"
                :active-index="activeIndex"
                :display-index="idx"
                v-model="modelData[item.value]"
                :popups-data="popupsData"
                :continue-disabled="isContinueDisabled(item, idx)"
                @continue="(isLast, emittedItem) => nextQuestion(isLast, idx, emittedItem)"
                @next-slide="(isLast, emittedItem) => nextQuestion(isLast, idx, emittedItem)"
                @show-popup="(content) => emit('show-popup', content)"
            />
          </SwiperSlide>
        </Swiper>

        <CustomPagination
            :active-index="activeIndex"
            :total-steps="questionsFlow.length"
            :can-go-back="canGoBack"
            @prev-question="prevQuestion"
            @go-to-slide="goToSlide"
        />
      </template>
    </div>
  </div>
</template>
